"use client";

import { useMemo, useState, useEffect, useContext, useRef } from "react";
import { DataGrid, GridFooterContainer, GridFooter } from "@mui/x-data-grid";
import {
  IconButton,
  Typography,
  Box,
  Paper,
  Tooltip,
  Chip,
  alpha,
  Button,
  TextField,
  InputAdornment,
  Autocomplete,
  Checkbox,
  Collapse,
  useMediaQuery,
  Drawer,
  Divider,
  Badge,
} from "@mui/material";
import { useTenantNavigation } from "../../hooks/useTenantNavigation";


import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Inventory2Icon from "@mui/icons-material/Inventory2";

// Icons
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import StorefrontIcon from "@mui/icons-material/Storefront";
import PaymentIcon from "@mui/icons-material/Payment";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import ReplayIcon from "@mui/icons-material/Replay";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import BadgeIcon from "@mui/icons-material/Badge";
import TuneIcon from "@mui/icons-material/Tune";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { ThemeProvider, createTheme } from "@mui/material/styles";
import dayjs from "dayjs";

import { ErpContext } from "../../context/ErpContext.js";
import "./TablaVentasComponent.css";
import SpinnerLading from "../misc/SpinnerComponent.js";
import ModalViewTablaCompra from "./ModalViewTablaCompra.js";
import CustomPagination from "./CustomPagination";

import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

import ModalMedioDePago from "./ModalMedioDePago.js";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#5b5b5f" },
    secondary: { main: "rgb(244, 177, 14)" },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": { boxShadow: "0 4px 8px rgba(0,0,0,0.1)" },
        },
      },
    },
    MuiPaper: { styleOverrides: { root: { borderRadius: 12 } } },
    MuiChip: { styleOverrides: { root: { fontWeight: 500 } } },
  },
});

export default function TablaCompras() {
  // Variables y funciones del context (sin cambios)
  const {
    getMoneda,
    getTipoTransaccion,
    getUbicacion,
    ubicaciones,
    getNegocio,
    getMedioDePago,
    getSubCategoriaTransaccion,
    getTransaccionItem,
    getTransaccionItemIDEnCompras,
    getTransaccionItemID,
    getItem,
    generarFacturaPDF,
    getItemUbicacion,
    getUsuario,
    getTransaccionTransformedCompras,
    getEntidadByTipoEntidad,
    getItemById,
    getItemUbicacionFilter,
    getEstadoCompra,
    getEntidadByID,
    updateDeleteTransaccion,
    isNavbarCollapsed,
    getCuentaCorrienteByIdEntidad,
    postCrearPago,
    updateCuentaCorriente,
    postCuentaCorriente,
    updateTransaccionCompraEstado,
    postTransaccionPago,
    tipoMedioDePago,
    getTipoMedioDePago,
    entidadAtributoNoEliminados,
    getEntidadAtributoNoEliminados,
    getEntidadAtributoClasificacionByIdAtributo,
  } = useContext(ErpContext);

  const navigate = useTenantNavigation();

    const estadoCompraGlobal = useRef(1); // Se actualiza de forma inmediata y no provoca re-render
  

  // Estados propios del componente
  const [openModal, setOpenModal] = useState(false);
  const [transaccionItems, setTransaccionItems] = useState([]);
  const [compraItems, setTransaccionCompraItems] = useState([]);
  const [selectedTransaccion, setSelectedTransaccion] = useState(null);
  const [selectedTransaccionEntidad, setSelectedTransaccionEntidad] =
    useState(null);
  const [loading, setLoading] = useState(true);
  const [sortedData, setSortedData] = useState([]);
  const [itemsMap, setItemsMap] = useState({});
  const [estadoSeleccionado, setEstadoSeleccionado] = useState(null);
  const [estados, setEstados] = useState([]);

  // Estado para proveedores (entidades) – queremos sólo las de ID 2
  const [entidades, setEntidades] = useState([]);

  // Estados para filtros
  const today = dayjs();
  const [startDate, setStartDate] = useState(today.subtract(30, "day"));
  const [endDate, setEndDate] = useState(today);
  const [minCompras, setMinCompras] = useState(0);
  const [maxCompras, setMaxCompras] = useState(0);
  const [proveedor, setProveedor] = useState(null);
  const [searchTextProveedor, setSearchTextProveedor] = useState("");
  const [selectedUbicacionFiltro, setSelectedUbicacionFiltro] = useState(null);
  const [page, setPage] = useState(null);
  const [limit, setLimit] = useState(100);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [mostrarDeletes, setMostrarDeletes] = useState(false);
  const [idTransaccionSearch, setIdTransaccionSearch] = useState(null);

  const [montoTotalTransacciones, setMontoTotalTransacciones] = useState(0)

  // ===== Responsive flags =====
  const isLaptopDown = useMediaQuery("(max-width:1200px)")
  const isMobile = useMediaQuery("(max-width:600px)")

  // Estados para el menú lateral (solo desktop)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [mostrarFiltroMontos, setMostrarFiltroMontos] = useState(false)
  const [mostrarFiltroProveedor, setMostrarFiltroProveedor] = useState(false)
  const [mostrarFiltroEstado, setMostrarFiltroEstado] = useState(false)

  // Estados para filtros de atributos de entidad
  const [opcionesPorAtributo, setOpcionesPorAtributo] = useState({})
  const [atributosEntidadFiltro, setAtributosEntidadFiltro] = useState({})

  const isFirstRender = useRef(true);

  //modal medio de pagos
  const [abrirMedioDePago, setAbrirMedioDePago] = useState(false);
  const [precioTotal, setPrecioTotal] = useState(0);


  // Funciones para manipular fechas y montos
  const increaseDate = () =>
    setStartDate((prev) => (prev ? prev.add(1, "day") : dayjs()));
  const decreaseDate = () =>
    setStartDate((prev) => (prev ? prev.subtract(1, "day") : dayjs()));
  const handleMinChange = (e) => {
    const newMin = Number(e.target.value);
    if (newMin <= maxCompras) setMinCompras(newMin);
  };
  const handleMaxChange = (e) => {
    const newMax = Number(e.target.value);
    if (newMax >= minCompras) setMaxCompras(newMax);
  };

  // ===== Control del menú lateral =====
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen)
  }

  // Función para alternar filtros y cerrar otros
  const toggleFiltro = (filtroNombre) => {
    switch (filtroNombre) {
      case 'montos':
        setMostrarFiltroMontos(!mostrarFiltroMontos)
        setMostrarFiltroProveedor(false)
        setMostrarFiltroEstado(false)
        break
      case 'proveedor':
        setMostrarFiltroProveedor(!mostrarFiltroProveedor)
        setMostrarFiltroMontos(false)
        setMostrarFiltroEstado(false)
        break
      case 'estado':
        setMostrarFiltroEstado(!mostrarFiltroEstado)
        setMostrarFiltroMontos(false)
        setMostrarFiltroProveedor(false)
        break
      default:
        break
    }
  }

  // Función para obtener las compras filtradas, pasando el id del estado si se selecciona
  const fetchData = async (
    date,
    page,
    endDate,
    proveedor,
    selectedUbicacionFiltro,
    valorMin,
    valorMax,
    limit = 100,
    estadoSeleccionado,
    mostrarDeletes,
    idTransaccionSearch,
    atributosEntidad = {}
  ) => {
    const controller = new AbortController();
    const signal = controller.signal;
    try {
      setLoading(true);
      const formattedDate = date.format("YYYY-MM-DD");
      const formattedDate2 =
        endDate && typeof endDate.format === "function"
          ? endDate.format("YYYY-MM-DD")
          : endDate;
      const idProveedor = proveedor ? proveedor.id : null;
      const idUbicacion = selectedUbicacionFiltro
        ? selectedUbicacionFiltro.id
        : null;

      const data = await getTransaccionTransformedCompras(
        2, // Compras
        formattedDate,
        formattedDate2,
        page,
        limit,
        { signal },
        idProveedor,
        idUbicacion,
        minCompras,
        maxCompras,
        estadoSeleccionado ? estadoSeleccionado.id : "",
        mostrarDeletes,
        idTransaccionSearch,
        atributosEntidad
      );

      console.log("Data de compras:", data);

      const transaccionesCompras = data.data || [];
      const transaccionesReforged = transaccionesCompras.map((transaccion) => {
        const pagos = transaccion.pagos.map((pago) => ({
          descripcion: pago.medioDePago,
          monto: pago.montoTotal,
        }));

        const formattedDate = new Intl.DateTimeFormat("es-AR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }).format(new Date(transaccion.fechaHoraCreacion));

        return {
          id: transaccion.id,
          idEstadoDeCompra:
            transaccion.transaccionCompraEstado?.estadoCompra.id ||
            "No disponible",
          createdAt: formattedDate,
          montoTotal: transaccion.montoTotal,
          idMoneda: transaccion.pagos?.[0]?.cotizacion || "",
          idTipoTransaccion: transaccion.tipoTransaccion?.descripcion || "",
          pagos,
          descripcionEntidad: transaccion.entidad?.descripcion || "",
          textoDescripcion: transaccion.descripcion || "",
          dniCuitCuil: transaccion.entidad?.dniCuitCuil || "",
          apellido: transaccion.entidad?.apellido || "",
          direccion: transaccion.entidad?.direccion || "",
          localidad: transaccion.entidad?.localidad || "",
          provincia: transaccion.entidad?.provincia || "",
          usuario: transaccion.usuario?.usuario || "",
          negocio: transaccion.negocio?.descripcion || "",
          ubicacion: transaccion.ubicacion?.descripcion || "",
          idEntidadNumero: transaccion.idEntidad,
          fechaHoraCreacion: transaccion.fechaHoraCreacion,
          impuestos: transaccion.impuestos,
          estadoDeCompra:
            transaccion.transaccionCompraEstado?.estadoCompra.descripcion ||
            "No disponible",
          idEstadoDeCompra:
            transaccion.transaccionCompraEstado?.estadoCompra.id ||
            "No disponible",
        };
      });

      setPage(data.currentPage);
      setTotalPages(data.totalPages);
      setTotalCount(data.total);
      setMontoTotalTransacciones(data.totalMontoVentas)
      setSortedData(transaccionesReforged.sort((a, b) => b.id - a.id));
    } catch (error) {
      console.error("Error al cargar las compras:", error);
    } finally {
      setLoading(false);
    }
  };

  // Botón Buscar: resetea la página a 1 y envía el id del estado (si está seleccionado)
  const buttonFiltrado = () => {
    setPage(1);
    fetchData(
      startDate,
      1,
      endDate,
      proveedor,
      selectedUbicacionFiltro,
      minCompras,
      maxCompras,
      100,
      estadoSeleccionado ? estadoSeleccionado : null,
      mostrarDeletes,
      idTransaccionSearch,
      atributosEntidadFiltro
    );
  };

  // Cargar opciones para cada atributo cuando cambian los atributos disponibles
  const cargarOpcionesAtributos = async () => {
    if (!entidadAtributoNoEliminados || entidadAtributoNoEliminados.length === 0) {
      setOpcionesPorAtributo({})
      return
    }

    const opcionesMap = {}
    for (const attr of entidadAtributoNoEliminados) {
      try {
        const atributoId = attr.idEntidadAtributo || attr.id
        const opciones = await getEntidadAtributoClasificacionByIdAtributo(atributoId, 2) // 2 = Proveedor
        opcionesMap[atributoId] = opciones || []
      } catch (error) {
        console.error(`Error al cargar opciones para atributo ${attr.idEntidadAtributo || attr.id}:`, error)
        opcionesMap[attr.idEntidadAtributo || attr.id] = []
      }
    }
    setOpcionesPorAtributo(opcionesMap)
  }

  // Inicializar opciones cuando se cargan los atributos disponibles
  useEffect(() => {
    if (entidadAtributoNoEliminados && entidadAtributoNoEliminados.length > 0) {
      cargarOpcionesAtributos()
    } else if (entidadAtributoNoEliminados && entidadAtributoNoEliminados.length === 0) {
      setOpcionesPorAtributo({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entidadAtributoNoEliminados])

  // Resetear filtros
  const resetFilters = () => {
    setProveedor(null);
    setSearchTextProveedor("");
    setSelectedUbicacionFiltro(null);
    setMinCompras(0);
    setMaxCompras(0);
    setEstadoSeleccionado(null);
    const today = dayjs();
    setStartDate(today.subtract(30, "day"));
    setEndDate(today);
    setPage(1);
    setMostrarDeletes(null);
    setIdTransaccionSearch("");
    setAtributosEntidadFiltro({});
    setMostrarFiltroMontos(false)
    setMostrarFiltroProveedor(false)
    setMostrarFiltroEstado(false)
    fetchData(
      today.subtract(30, "day"),
      1,
      today,
      null,
      null,
      0,
      0,
      100,
      null,
      null,
      null,
      {}
    );
  };

  // Carga inicial (se ejecuta una sola vez)
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchInicio = async () => {
      try {
        setPage(1);
        await getMoneda();
        await getTipoTransaccion();
        await getUbicacion();
        await getNegocio();
        // Obtener proveedores: solo las entidades con ID 2
        const proveedores = await getEntidadByTipoEntidad(2);
        console.log("proveedores ", proveedores);
        const estados = await getEstadoCompra();
        // Aseguramos que sea un array
        setEntidades(Array.isArray(proveedores) ? proveedores : [proveedores]);
        setEstados(Array.isArray(estados) ? estados : [estados]);
        await getMedioDePago();
        await getSubCategoriaTransaccion();
        await getTransaccionItem();
        await getItem();
        await getItemUbicacion();
        await getUsuario();

        // Cargar atributos de entidad Proveedor (ID 2)
        await getEntidadAtributoNoEliminados(2)

        // Cargar compras con filtros por defecto
        //fetchData(startDate, 1, endDate, proveedor, selectedUbicacionFiltro, minCompras, maxCompras, 100, estadoSeleccionado ? estadoSeleccionado : null)
      } catch (error) {
        console.error("Error al cargar datos iniciales:", error);
      }
    };

    fetchInicio();

    return () => {
      controller.abort();
    };
  }, []);

  // Actualizar datos cuando cambia la página (para evitar ejecuciones duplicadas)
  useEffect(() => {
    if (!isFirstRender.current) {
      console.log("entro aca el renderrrrrrrr");
      fetchData(
        startDate,
        page,
        endDate,
        proveedor,
        selectedUbicacionFiltro,
        minCompras,
        maxCompras,
        100,
        estadoSeleccionado ? estadoSeleccionado : null,
        mostrarDeletes,
        idTransaccionSearch,
        atributosEntidadFiltro
      );
    } else {
      isFirstRender.current = false;
    }
  }, [page]);

  // Definición de columnas (sin cambios)
  const columns = [
    { field: "id", headerName: "ID", width: 70, headerAlign: "center", },
    {
      field: "createdAt",
      headerName: "Fecha",
      width: 200,
      flex: 1,
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CalendarTodayIcon
            fontSize="small"
            sx={{ color: alpha("#5b5b5f", 0.7) }}
          />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "montoTotal",
      headerName: "Monto",
      type: "number",
      width: 130,
      flex: 1,
      headerAlign: "center",
      renderCell: (params) => (
        <Chip
          icon={<AttachMoneyIcon fontSize="small" />}
          label={params.value?.toLocaleString("es-AR") || "0.00"}
          size="small"
          sx={{
            fontWeight: "bold",
            bgcolor: alpha("#4CAF50", 0.1),
            color: "#4CAF50",
            "& .MuiChip-icon": { color: "#4CAF50" },
            display: "flex",
          }}
        />
      ),
    },
    {
      field: "pagos",
      headerName: "Medio de Pago",
      width: 200,
      flex: 1,
      headerAlign: "center",
      renderCell: (params) => {
        const pagos = Array.isArray(params.row.pagos) ? params.row.pagos : [];
        if (pagos.length > 1) {
          return (
            <Chip
              label="Múltiple"
              size="small"
              color="info"
              sx={{ fontWeight: "bold", display: "flex" }}
            />
          );
        }
        return (
          <Chip
            icon={<PaymentIcon fontSize="small" />}
            label={pagos[0]?.descripcion || "N/A"}
            size="small"
            variant="outlined"
            sx={{
              fontWeight: "bold",
              color: "#00AAE4",
              borderColor: alpha("#00AAE4", 0.5),
              "& .MuiChip-icon": { color: "#00AAE4" },
              display: "flex",
            }}
          />
        );
      },
    },
    {
      field: "descripcionEntidad",
      headerName: "Proveedor",
      width: 150,
      flex: 1,
      headerAlign: "center",
      renderCell: (params) => (
        <Tooltip
          title={`${params.row.descripcionEntidad} ${
            params.row.apellido || ""
          }`}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PersonIcon
              fontSize="small"
              sx={{ color: alpha("#5b5b5f", 0.7) }}
            />
            <Typography variant="body2" noWrap>
              {params.row.descripcionEntidad} {params.row.apellido || ""}
            </Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "negocio",
      headerName: "Negocio",
      width: 150,
      flex: 1,
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <StorefrontIcon
            fontSize="small"
            sx={{ color: alpha("#5b5b5f", 0.7) }}
          />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "ubicacion",
      headerName: "Ubicación",
      width: 150,
      flex: 1,
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LocationOnIcon
            fontSize="small"
            sx={{ color: alpha("#5b5b5f", 0.7) }}
          />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "action",
      headerName: "Acciones",
      width: 150,
      flex: 1,
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="Ver detalles">
            <IconButton
              className="colorGeneralParaIconos"
              onClick={() => handleOpenModal(params.row)}
              size="small"
              sx={{
                bgcolor: alpha("#5b5b5f", 0.1),
                "&:hover": { bgcolor: alpha("#5b5b5f", 0.2) },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Descargar PDF">
            <IconButton
              className="colorGeneralParaIconos"
              onClick={() => handleDownloadPDF(params.row)}
              size="small"
              sx={{
                bgcolor: alpha("rgb(244, 177, 14)", 0.1),
                "&:hover": { bgcolor: alpha("rgb(244, 177, 14)", 0.2) },
              }}
            >
              <PictureAsPdfIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton
              className="colorGeneralParaIconos"
              onClick={() => handleEditarTransaccion(params.row)}
              size="small"
              sx={{
                bgcolor: alpha("rgb(244, 177, 14)", 0.1),
                "&:hover": { bgcolor: alpha("rgb(244, 177, 14)", 0.2) },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
           <Tooltip title="Pagar">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenMediosDePago(params.row)}
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary.main, 0.2),
                            },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
          <Tooltip title="Borrar Transaccion">
            <IconButton
              className="colorGeneralParaIconos"
              onClick={() => borrarTransaccion(params.row)}
              size="small"
              sx={{
                bgcolor: alpha("rgb(244, 177, 14)", 0.1),
                "&:hover": { bgcolor: alpha("rgb(244, 177, 14)", 0.2) },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "estadoDeCompra",
      headerName: "Estado De Compra",
      width: 150,
      renderCell: (params) => {
        const description = params.value || "";
        const idEstado = params.row.idEstadoDeCompra;
        let textColor = "inherit";
        if (idEstado === 1) textColor = "orange";
        else if (idEstado === 2) textColor = "red";
        else if (idEstado === 3) textColor = "green";
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PaymentIcon
              fontSize="small"
              sx={{ color: alpha("#5b5b5f", 0.7) }}
            />
            <Typography
              variant="body2"
              sx={{ color: textColor, fontWeight: "bold" }}
            >
              {description}
            </Typography>
          </Box>
        );
      },
    },
  ];

  const borrarTransaccion = async (transaccionRow) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: `Vas a eliminar la transacción con ID ${transaccionRow.id}. Esta acción no se puede deshacer.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        // ejecuta la eliminación
        await updateDeleteTransaccion(transaccionRow.id);

        // refresca los datos (ajusta los args a tu fetchData)
        await fetchData(
          startDate,
          1,
          endDate,
          proveedor,
          selectedUbicacionFiltro,
          minCompras,
          maxCompras,
          100,
          estadoSeleccionado ? estadoSeleccionado : null,
          mostrarDeletes,
          idTransaccionSearch,
          atributosEntidadFiltro
        );

        Swal.fire({
          title: "Eliminado",
          text: `La transacción ${transaccionRow.id} ha sido eliminada.`,
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Error al borrar transacción:", error);
        Swal.fire({
          title: "Error",
          text: "No se pudo eliminar la transacción. Intenta de nuevo.",
          icon: "error",
        });
      }
    }
  };

  const totalCompras = useMemo(() => {
    if (!sortedData || !Array.isArray(sortedData) || sortedData.length === 0)
      return 0;
    return sortedData.reduce((acc, row) => acc + (row.montoTotal || 0), 0);
  }, [sortedData]);

  const CustomFooter = () => (
    <GridFooterContainer>
      <div
        style={{
          padding: "10px",
          fontWeight: "bold",
          fontSize: "16px",
          textAlign: "right",
          width: "100%",
          backgroundColor: "#f5f5f5",
          borderTop: "1px solid #ddd",
        }}
      >
        Total Compras:{" "}
        <span style={{ color: "#4CAF50" }}>
          ${totalCompras.toLocaleString("es-AR")}
        </span>
      </div>
      <GridFooter />
    </GridFooterContainer>
  );

  const handleEditarTransaccion = (transaccionRow) => {
    navigate(
      `editarCompraComponent/${transaccionRow.id}/${transaccionRow.idEstadoDeCompra}`
    );
  };

  const handleDownloadPDF = async (transaccionRow) => {
    try {
      const transaccionEncontrada = await getTransaccionItemID(
        transaccionRow.id
      );
      console.log("transaccionEncontrada: ", transaccionEncontrada)
      // Como solo queremos proveedores (entidades con ID 2), usamos getEntidadByID(2)
      const entidadProveedor = await getEntidadByID(2);
      const itemsTransaccion = await Promise.all(
        transaccionEncontrada.map(async (transItem) => {
          const foundItem = await getItemById(transItem.idItem)
          const precio = transItem.precio || 0
          return {
            idItem: transItem.idItem,
            codigo: foundItem ? foundItem.codigo : "N/A",
            descripcion: foundItem ? foundItem.descripcion : "Descripción no disponible",
            cantidad: transItem.cantidad || 0,
            precio: precio,
            porcentajeDescuento: transItem.porcentajeDescuento || 0,
            total: (precio * transItem.cantidad).toFixed(2),
          }
        }),
      )
      generarFacturaPDF(
        transaccionRow,
        itemsTransaccion,
        entidadProveedor,
        "tablaCompra",
        transaccionRow.impuestos
      );
    } catch (error) {
      console.error("Error al manejar la descarga del PDF:", error);
    }
  };

  // Obtener detalles de items relacionados
  useEffect(() => {
    const fetchItemsDetails = async () => {
      const itemsDetails = {};
      await Promise.all(
        transaccionItems.map(async (transaccionItem) => {
          const foundItem = await getItemById(transaccionItem.idItem);
          if (foundItem) {
            itemsDetails[transaccionItem.idItem] = foundItem;
          }
        })
      );
      setItemsMap(itemsDetails);
    };
    if (transaccionItems.length) {
      fetchItemsDetails();
    }
  }, [transaccionItems, getItemById]);

  const handleOpenModal = async (transaccionRow) => {
    try {
      const transaccionSelect = sortedData.find(
        (t) => t.id === transaccionRow.id
      );
      setSelectedTransaccion(transaccionSelect);
      
      // Obtener la entidad correcta de la transacción (no hardcodeada)
      if (transaccionRow.idEntidadNumero && transaccionRow.idEntidadNumero !== null) {
        try {
          const entidadProveedor = await getEntidadByID(transaccionRow.idEntidadNumero);
          setSelectedTransaccionEntidad(entidadProveedor);
        } catch (error) {
          console.error("Error al obtener entidad:", error);
          setSelectedTransaccionEntidad(null);
        }
      } else {
        setSelectedTransaccionEntidad(null);
      }
      
      const items = await getTransaccionItemIDEnCompras(transaccionRow.id);
      setTransaccionItems(items.items);
      setTransaccionCompraItems(items.compraItems);
      setOpenModal(true);
    } catch (error) {
      console.error("Error al abrir el modal:", error);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  // Función para cerrar el modal de gráficas
  const handleCloseMedioDePago = () => {
    setAbrirMedioDePago(false);
  };

  const handleOpenMediosDePago = (datos) => {
    console.log("Datos recibidos para abrir medio de pago:", datos, datos.estadoDeCompra);
    if(datos.estadoDeCompra != "Cerrada" && datos.estadoDeCompra != "Stock Pendiente") {
      const updatedTotalMedioPagoList = datos.pagos.reduce((total, pago) => total + pago.monto, 0)
      console.log(updatedTotalMedioPagoList)
      setPrecioTotal(Math.abs(datos.montoTotal - updatedTotalMedioPagoList)); // Usar valor absoluto del saldo
      setSelectedTransaccion(datos); // Guardar los datos de la cuenta
      setAbrirMedioDePago(true);
    }
  };

  const handleConfirmarPago = async (
    mediosDePago,
    montoTotalMediosPago,
    transaccionSeleccionada,
    descripcion
  ) => {
    // Calcular si es pago completo o parcial
    console.log(mediosDePago)
        console.log(montoTotalMediosPago)

console.log(transaccionSeleccionada)

    const esPagoCompleto =
      Math.abs(montoTotalMediosPago) >= Math.abs(transaccionSeleccionada?.saldo);

    const descripcionFinal = descripcion || "Pago cuenta corriente";

      const procesarTransaccion = async (dato, cerrarCompra = false) => {
          try {
      
            console.log(dato);
              //Verificamos si restante es 0
              const restante = precioTotal - montoTotalMediosPago;
                console.log(dato);
                // Procesar medios de pago
                if (mediosDePago) {
                    for (const it of mediosDePago) {

                      const transaccionCrearPago = await postCrearPago(
                        1,
                        1,
                        it.id,
                        it.monto
                      );

                      const idTransaccionCrearPago = transaccionCrearPago.data.id;


                      // Verificar si el tipo de medio de pago afecta cuenta corriente usando el flag
                      const tipoMedio = tipoMedioDePago.find(t => t.id === it.tipoMedioDePago)
                      if (tipoMedio?.afectaCuentaCorriente === true) {
                        const cuentas = await getCuentaCorrienteByIdEntidad(
                          transaccionSeleccionada.idEntidadNumero
                        ); //Todas las cuentas relacionadas a esta cuenta
                        const cuenta = cuentas.find( //Verificamos si existe esta cuenta o si hay que crearla
                          (ct) => ct.idEntidad === transaccionSeleccionada.idEntidadNumero
                        );
      
                        if (cuenta) {
                          await updateCuentaCorriente(
                            transaccionSeleccionada.idEntidadNumero,
                            it.monto,
                            "compra" //va compra para restar la cuenta corriente
                          );
                        } else {
                          await postCuentaCorriente(
                            transaccionSeleccionada.idEntidadNumero,
                            it.monto,
                            "compra" //va compra para restar la cuenta corriente
                          );
                        }
                      }
                      //Post transaccion del pago
      
                      await postTransaccionPago(
                        transaccionSeleccionada.id, //idTransaccion
                        idTransaccionCrearPago
                      );
                    }
                  
                }
      
            if (cerrarCompra) {
              console.log("Usuario eligió cerrar la compra. Estado: CERRADA");
              estadoCompraGlobal.current = 3; // Actualiza el estado global
              await updateTransaccionCompraEstado(transaccionSeleccionada.id, 3); // CERRADA
            } else {
              // 2. ¿Pagado completo?
              let estaPagado = false;
              const restante = precioTotal - montoTotalMediosPago;
      

                // Múltiples medios de pago → la suma debe cubrir el restante
                const sumaMedios = mediosDePago.reduce(
                  (total, item) => total + item.monto,
                  0
                );
                estaPagado = sumaMedios >= restante;
                            console.log('NUMEROS ',precioTotal, montoTotalMediosPago, restante, sumaMedios, estaPagado)


              // 1. ¿Stock completo?
              let stockSatisfecho = false;
              if (dato == "stock") {
                stockSatisfecho = transaccionItems.every(
                  (item) =>
                    (item.cantidadRecibida || 0) + (item.aRecibir || 0) >=
                    item.cantidad
                );
              }
              if (dato == "pagos") {
                stockSatisfecho = transaccionItems.every(
                  (item) => (item.cantidadRecibida || 0) >= item.cantidad
                );
              }
      
              if (!estaPagado && !stockSatisfecho) {
                console.log("No pagada y stock incompleto. Estado: ABIERTA");
                estadoCompraGlobal.current = 1;
                await updateTransaccionCompraEstado(transaccionSeleccionada.id, 1); // ABIERTA
              } else if (!estaPagado && stockSatisfecho) {
                console.log("No pagada. Estado: SALDO PENDIENTE");
                estadoCompraGlobal.current = 4;
                await updateTransaccionCompraEstado(transaccionSeleccionada.id, 4); // SALDO PENDIENTE
              } else if (estaPagado && !stockSatisfecho) {
                console.log("Pagada pero stock incompleto. Estado: SALDO CERRADO");
                estadoCompraGlobal.current = 5;
                await updateTransaccionCompraEstado(transaccionSeleccionada.id, 5); // SALDO CERRADO
              } else if (estaPagado && stockSatisfecho) {
                setAbrirMedioDePago(false);
                // Usamos SweetAlert2 para esperar la respuesta del usuario
                const MySwal = withReactContent(Swal);
                const result = await MySwal.fire({
                  title: "Confirmar cierre",
                  text: "La compra cumple los requisitos para ser cerrada. ¿Desea cerrarla ahora?",
                  icon: "question",
                  showCancelButton: true,
                  confirmButtonText: "Cerrar compra",
                  cancelButtonText: "No cerrar",
                  customClass: {
    container: 'swal2-override-zindex'
  }
                });
      
                // Si el usuario cancela, detenemos la función (o realizamos otra acción)
                if (!result.isConfirmed) {
                  estadoCompraGlobal.current = 1; // Actualiza el estado global
                  // Por ejemplo, llamamos a una función para dejarla en estado "sin cerrar"
                  await convertirSinCerrar(transaccionSeleccionada.id);
                  return; // Salimos de la función
                } else {
                  estadoCompraGlobal.current = 3; // Actualiza el estado global
                  // El usuario confirmó: procedemos a cerrar la compra
                  await cerrarLaCompra(transaccionSeleccionada.id);
                }
              }
            }
      
            withReactContent(Swal).fire({
              position: "bottom-end",
              icon: "success",
              text: "Compra actualizada correctamente",
              showConfirmButton: false,
              timer: 3000,
              width: 300,
            });
          } catch (error) {
            console.error("Error al actualizar la transacción:", error);
            withReactContent(Swal).fire({
              position: "bottom-end",
              icon: "error",
              text: "Error al actualizar la compra",
              showConfirmButton: false,
              timer: 3000,
              width: 300,
            });
          }
        };

        await procesarTransaccion("pagos");
        setAbrirMedioDePago(false);
        await fetchData(
      startDate,
      1,
      endDate,
      proveedor,
      selectedUbicacionFiltro,
      minCompras,
      maxCompras,
      100,
      estadoSeleccionado ? estadoSeleccionado : null,
      mostrarDeletes,
      idTransaccionSearch,
      atributosEntidadFiltro
    );
    
  };

    const convertirSinCerrar = async (idTransaccionEditarCompra) => {
    estadoCompraGlobal.current = 1; // Actualiza el estado global
    await updateTransaccionCompraEstado(idTransaccionEditarCompra, 1); // ABIERTA
  };
  const cerrarLaCompra = async (idTransaccionEditarCompra) => {
    estadoCompraGlobal.current = 3; // Actualiza el estado global
    await updateTransaccionCompraEstado(idTransaccionEditarCompra, 3); //CERRADA
  };



  // Función para renderizar el contenido de los filtros (reutilizable)
  const renderFiltrosContent = () => (
    <>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              alignItems: "center",
              justifyContent: "space-between",
          flexDirection: isLaptopDown ? "column" : "row",
        }}
      >
        {/* Filtro de fechas - PRIMERA PRIORIDAD */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flex: 1, minWidth: isLaptopDown ? "100%" : 250, width: isLaptopDown ? "100%" : "auto" }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <IconButton onClick={decreaseDate} color="primary" size="small">
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <DatePicker
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  format="DD/MM/YYYY"
                  label="Desde"
              slotProps={{ textField: { size: "small", inputProps: { maxLength: 10 }, fullWidth: isLaptopDown } }}
              sx={{ flex: 1 }}
                />
                <DatePicker
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  format="DD/MM/YYYY"
                  label="Hasta"
              slotProps={{ textField: { size: "small", inputProps: { maxLength: 10 }, fullWidth: isLaptopDown } }}
              sx={{ flex: 1 }}
                />
                <IconButton onClick={increaseDate} color="primary" size="small">
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </LocalizationProvider>
            </Box>

        {/* Filtro de ID, ubicación y proveedor - SEGUNDA PRIORIDAD */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 2, minWidth: isLaptopDown ? "100%" : 300, width: isLaptopDown ? "100%" : "auto", flexDirection: isLaptopDown ? "column" : "row" }}>
          <TextField
            label="Buscar por ID"
            type="number"
            size="small"
            value={idTransaccionSearch}
            onChange={(e) => setIdTransaccionSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: isLaptopDown ? "100%" : "20%" }}
            fullWidth={isLaptopDown}
          />
          <Autocomplete
            size="small"
            disablePortal
            options={Array.isArray(ubicaciones) ? ubicaciones.map((it) => ({ id: it.id, label: it.descripcion })) : []}
            value={selectedUbicacionFiltro}
            onChange={(event, newValue) => setSelectedUbicacionFiltro(newValue)}
            sx={{ flex: 1, width: isLaptopDown ? "100%" : "auto" }}
            renderInput={(params) => <TextField {...params} label="Ubicación" variant="outlined" />}
          />
          <Autocomplete
            size="small"
            disablePortal
            options={Array.isArray(entidades) ? entidades.map((it) => ({
              label: `${it.descripcion} - ${it.apellido} - ${it.dniCuitCuil}`,
              ...it,
            })) : []}
            value={proveedor}
            onChange={(event, newValue) => {
              setProveedor(newValue || null)
            }}
            onInputChange={(event, newInputValue) => setSearchTextProveedor(newInputValue)}
            sx={{ flex: 1, width: isLaptopDown ? "100%" : "auto" }}
            renderInput={(params) => <TextField {...params} label="Proveedor - DNI / CUIT / CUIL" variant="outlined" />}
          />
        </Box>

        {/* Botones de acción (solo desktop) */}
        {!isLaptopDown && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={buttonFiltrado}
              startIcon={<SearchIcon />}
              sx={{
                backgroundColor: "#5b5b5f",
                textTransform: "none",
                padding: "8px 16px",
                color: "white",
                "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
              }}
            >
              Buscar
            </Button>

            <Tooltip title={proveedor || selectedUbicacionFiltro || minCompras > 0 || maxCompras > 0 || estadoSeleccionado || Object.values(atributosEntidadFiltro).some(v => v) ? "Resetear filtros" : "Refrescar"}>
              <IconButton
                onClick={resetFilters}
                color="primary"
                size="small"
                sx={{
                  bgcolor: proveedor || selectedUbicacionFiltro || minCompras > 0 || maxCompras > 0 || estadoSeleccionado || Object.values(atributosEntidadFiltro).some(v => v) ? alpha(theme.palette.secondary.main, 0.1) : "transparent",
                  "&:hover": { bgcolor: alpha(theme.palette.secondary.main, 0.2) },
                }}
              >
                <ReplayIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={sidebarOpen ? "Cerrar Más Filtros" : "Mostrar Más Filtros"}>
              <Badge 
                badgeContent={
                  (minCompras > 0 || maxCompras > 0 ? 1 : 0) +
                  (estadoSeleccionado ? 1 : 0) +
                  (Object.values(atributosEntidadFiltro).filter(v => v).length > 0 ? 1 : 0)
                } 
                color="secondary"
              >
              <IconButton
                  onClick={toggleSidebar}
                color="primary"
                size="small"
                sx={{
                    bgcolor: sidebarOpen ? alpha(theme.palette.secondary.main, 0.1) : "transparent",
                  "&:hover": { bgcolor: alpha(theme.palette.secondary.main, 0.2) },
                }}
              >
                <TuneIcon />
              </IconButton>
              </Badge>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Boxes de filtros activos (debajo del menú principal) */}
      {!isLaptopDown && (minCompras > 0 || maxCompras > 0 || proveedor || selectedUbicacionFiltro || estadoSeleccionado || Object.values(atributosEntidadFiltro).some(v => v)) && (
            <Box
              sx={{
            px: 0,
            pt: 2,
            pb: 0,
                display: "flex",
            flexWrap: "wrap",
                gap: 1,
            alignItems: "center",
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            mt: 2,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mr: 1, color: theme.palette.text.secondary }}>
            Filtros activos:
          </Typography>
          
          {/* Chip de Montos */}
          {(minCompras > 0 || maxCompras > 0) && (
            <Chip
              label={`Montos: ${minCompras > 0 ? `$${minCompras.toLocaleString("es-AR")}` : "Sin mínimo"} - ${maxCompras > 0 ? `$${maxCompras.toLocaleString("es-AR")}` : "Sin máximo"}`}
              size="small"
              onDelete={() => {
                setMinCompras(0)
                setMaxCompras(0)
                setMostrarFiltroMontos(false)
                buttonFiltrado()
              }}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
            />
          )}

          {/* Chip de Proveedor */}
          {proveedor && (
            <Chip
              label={`Proveedor: ${proveedor.descripcion || proveedor.label || proveedor.dniCuitCuil || ""}`}
              size="small"
              onDelete={() => {
                setProveedor(null)
                setSearchTextProveedor("")
                setMostrarFiltroProveedor(false)
                buttonFiltrado()
              }}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
            />
          )}

          {/* Chip de Ubicación */}
          {selectedUbicacionFiltro && (
            <Chip
              label={`Ubicación: ${selectedUbicacionFiltro.label || selectedUbicacionFiltro.descripcion || ""}`}
              size="small"
              onDelete={() => {
                setSelectedUbicacionFiltro(null)
                buttonFiltrado()
              }}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
            />
          )}

          {/* Chip de Estado */}
          {estadoSeleccionado && (
            <Chip
              label={`Estado: ${estadoSeleccionado.label || estadoSeleccionado.descripcion || ""}`}
              size="small"
              onDelete={() => {
                setEstadoSeleccionado(null)
                setMostrarFiltroEstado(false)
                buttonFiltrado()
              }}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
            />
          )}

          {/* Chips de Atributos de Entidad */}
          {entidadAtributoNoEliminados && entidadAtributoNoEliminados.length > 0 && entidadAtributoNoEliminados.map((atributo, index) => {
            const atributoNumero = index + 1
            const valorAtributo = atributosEntidadFiltro[atributoNumero]
            if (!valorAtributo) return null
            
            const atributoId = atributo.idEntidadAtributo || atributo.id
            const opciones = opcionesPorAtributo[atributoId] || []
            const opcionSeleccionada = opciones.find(o => o.id === valorAtributo)
            
            return (
              <Chip
                key={atributoId}
                label={`${atributo.descripcion}: ${opcionSeleccionada?.descripcion || "N/A"}`}
                size="small"
                onDelete={() => {
                  setAtributosEntidadFiltro(prev => {
                    const newAtributos = { ...prev }
                    delete newAtributos[atributoNumero]
                    return newAtributos
                  })
                  buttonFiltrado()
                }}
                sx={{
                  backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                  color: theme.palette.secondary.main,
                  fontWeight: 500,
                }}
              />
            )
          })}
        </Box>
      )}

      {/* Boxes de filtros expandibles (debajo del menú principal) */}
      {!isLaptopDown && (mostrarFiltroMontos || mostrarFiltroProveedor || mostrarFiltroEstado) && (
        <Box
          sx={{
            px: 0,
            pt: 2,
            pb: 0,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            mt: 2,
          }}
        >
          {/* Subbox de Montos */}
          {mostrarFiltroMontos && (
            <Paper
              elevation={2}
              sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.background.default, 0.5),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600, color: "#5b5b5f" }}>
                  <AttachMoneyIcon fontSize="small" sx={{ color: "rgb(244, 177, 14)" }} />
                  Rango de Montos
                </Typography>
                <IconButton size="small" onClick={() => setMostrarFiltroMontos(false)} sx={{ color: "#5b5b5f" }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TextField
                label="Mínimo"
                type="number"
                size="small"
                value={minCompras}
                onChange={handleMinChange}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  sx={{ flex: 1 }}
              />
              <TextField
                label="Máximo"
                type="number"
                size="small"
                value={maxCompras}
                onChange={handleMaxChange}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={buttonFiltrado}
                  startIcon={<SearchIcon sx={{ color: "white" }} />}
              sx={{
                    backgroundColor: "#5b5b5f",
                    textTransform: "none",
                    color: "white",
                    "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
                    "& .MuiButton-startIcon svg": {
                      color: "white",
                    },
                  }}
                >
                  Aplicar
                </Button>
                {(minCompras > 0 || maxCompras > 0) && (
                  <Button
                size="small"
                    variant="outlined"
                    onClick={() => {
                      setMinCompras(0)
                      setMaxCompras(0)
                      buttonFiltrado()
                    }}
                    sx={{ 
                      textTransform: "none",
                      color: "#5b5b5f",
                      borderColor: "#5b5b5f",
                      "&:hover": {
                        borderColor: "rgb(244, 177, 14)",
                        backgroundColor: alpha("rgb(244, 177, 14)", 0.1),
                      },
                    }}
                  >
                    Limpiar
                  </Button>
                )}
              </Box>
            </Paper>
          )}

          {/* Subbox de Atributos de Entidad */}
          {mostrarFiltroProveedor && (
            <Paper
              elevation={2}
              sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.background.default, 0.5),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600, color: "#5b5b5f" }}>
                  <PersonIcon fontSize="small" sx={{ color: "rgb(244, 177, 14)" }} />
                  Filtrar por Atributos de Entidad
                </Typography>
                <IconButton size="small" onClick={() => setMostrarFiltroProveedor(false)} sx={{ color: "#5b5b5f" }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Filtros por Atributos de Entidad */}
              <Box sx={{ mb: 2 }}>
                {entidadAtributoNoEliminados && Array.isArray(entidadAtributoNoEliminados) && entidadAtributoNoEliminados.length > 0 && (
                  <Chip 
                    label={`${entidadAtributoNoEliminados.length} atributos disponibles`} 
                    size="small" 
                    sx={{ mb: 2, color: "#5b5b5f" }} 
                  />
                )}
                
                {(() => {
                  const tieneAtributos = entidadAtributoNoEliminados && Array.isArray(entidadAtributoNoEliminados) && entidadAtributoNoEliminados.length > 0
                  
                  if (!tieneAtributos) {
                    return (
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontStyle: "italic" }}>
                        No hay atributos de entidad configurados. Ve a Administración → Atributos de Entidad para configurarlos.
                      </Typography>
                    )
                  }
                  
                  return (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" },
                        gap: 2,
                      }}
                    >
                      {entidadAtributoNoEliminados.map((atributo, index) => {
                        const atributoNumero = index + 1
                        const atributoId = atributo.idEntidadAtributo || atributo.id
                        const opciones = opcionesPorAtributo[atributoId] || []
                        
                        return (
              <Autocomplete
                            key={atributoId}
                size="small"
                disablePortal
                            options={opciones}
                            getOptionLabel={(option) => option.descripcion || ""}
                            value={opciones.find(o => o.id === atributosEntidadFiltro[atributoNumero]) || null}
                            onChange={(event, newValue) => {
                              setAtributosEntidadFiltro(prev => {
                                const newAtributos = { ...prev }
                                if (newValue) {
                                  newAtributos[atributoNumero] = newValue.id
                                } else {
                                  delete newAtributos[atributoNumero]
                                }
                                return newAtributos
                              })
                            }}
                renderInput={(params) => (
                              <TextField
                                {...params}
                                label={atributo.descripcion}
                                variant="outlined"
                                size="small"
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <>
                                      <InputAdornment position="start">
                                        <PersonIcon fontSize="small" sx={{ color: "rgb(244, 177, 14)" }} />
                                      </InputAdornment>
                                      {params.InputProps.startAdornment}
                                    </>
                                  ),
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    backgroundColor: atributosEntidadFiltro[atributoNumero]
                                      ? alpha(theme.palette.secondary.main, 0.05)
                                      : "white",
                                  },
                                }}
                              />
                            )}
                          />
                        )
                      })}
              </Box>
                  )
                })()}
            </Box>

              {/* Botones de Acción */}
              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={buttonFiltrado}
                  startIcon={<SearchIcon sx={{ color: "white" }} />}
                sx={{
                    backgroundColor: "#5b5b5f",
                    textTransform: "none",
                    color: "white",
                    "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
                    "& .MuiButton-startIcon svg": {
                      color: "white",
                    },
                  }}
                >
                  Aplicar Filtros
                </Button>
                {Object.values(atributosEntidadFiltro).filter(v => v).length > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setAtributosEntidadFiltro({})
                      buttonFiltrado()
                    }}
                    sx={{ 
                      textTransform: "none",
                      color: "#5b5b5f",
                      borderColor: "#5b5b5f",
                      "&:hover": {
                        borderColor: "rgb(244, 177, 14)",
                        backgroundColor: alpha("rgb(244, 177, 14)", 0.1),
                      },
                    }}
                  >
                    Limpiar Atributos
                  </Button>
                )}
              </Box>
            </Paper>
          )}

          {/* Subbox de Estado */}
          {mostrarFiltroEstado && (
            <Paper
              elevation={2}
                      sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.background.default, 0.5),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600, color: "#5b5b5f" }}>
                  <CheckCircleIcon fontSize="small" sx={{ color: "rgb(244, 177, 14)" }} />
                  Estado de Compra
                      </Typography>
                <IconButton size="small" onClick={() => setMostrarFiltroEstado(false)} sx={{ color: "#5b5b5f" }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                      <Autocomplete
                        size="small"
                        disablePortal
                  options={Array.isArray(estados) ? estados.map((it) => ({
                    id: it.id,
                    label: it.descripcion,
                  })) : []}
                  value={estadoSeleccionado}
                        onChange={(event, newValue) => {
                    setEstadoSeleccionado(newValue)
                        }}
                  getOptionLabel={(option) => option.label || ""}
                  sx={{ flex: 1, minWidth: 200 }}
                  renderInput={(params) => <TextField {...params} label="Estado de Compra" variant="outlined" size="small" />}
                      />
                    </Box>

              <Box sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                size="small"
                onClick={buttonFiltrado}
                startIcon={<SearchIcon sx={{ color: "white" }} />}
                sx={{
                  backgroundColor: "#5b5b5f",
                  textTransform: "none",
                  color: "white",
                    "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
                    "& .MuiButton-startIcon svg": {
                      color: "white",
                    },
                }}
              >
                  Aplicar
              </Button>
                {estadoSeleccionado && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setEstadoSeleccionado(null)
                      buttonFiltrado()
                    }}
                    sx={{ 
                      textTransform: "none",
                      color: "#5b5b5f",
                      borderColor: "#5b5b5f",
                      "&:hover": {
                        borderColor: "rgb(244, 177, 14)",
                        backgroundColor: alpha("rgb(244, 177, 14)", 0.1),
                      },
                    }}
                  >
                    Limpiar
                  </Button>
                )}
              </Box>
            </Paper>
          )}

        </Box>
      )}
    </>
  )

  if (loading) {
    return <SpinnerLading />
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <ThemeProvider theme={theme}>
        {/* Contenedor de filtros para Desktop (siempre visible) */}
        {!isLaptopDown && (
          <Collapse in={true} timeout={250}>
            <Paper
              className="diseñoFiltroTransaccionesVenta"
              elevation={3}
                  sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.background.paper, 0.9),
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                maxHeight: "40vh",
                overflow: "auto",
                flexShrink: 0,
                "&::-webkit-scrollbar": {
                  width: "8px",
                  height: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.4),
                  borderRadius: "4px",
                    "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.6),
                  },
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: alpha(theme.palette.grey[300], 0.3),
                  borderRadius: "4px",
                    },
                  }}
                >
              {renderFiltrosContent()}
        </Paper>
          </Collapse>
        )}

        {/* Menú lateral (solo desktop) */}
        {!isLaptopDown && (
          <Drawer
            anchor="right"
            open={sidebarOpen}
            onClose={toggleSidebar}
            variant="persistent"
            sx={{
              "& .MuiDrawer-paper": {
                width: 80,
                top: 0,
                height: "100vh",
                backgroundColor: alpha(theme.palette.background.paper, 0.98),
                borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
                zIndex: 1200,
                overflowX: "hidden",
              },
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", height: "100%", p: 1 }}>
              {/* Botón de cerrar */}
        <Box
          sx={{
            display: "flex",
                  justifyContent: "center",
            alignItems: "center",
            mb: 2,
                  pb: 1,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <IconButton size="small" onClick={toggleSidebar}>
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {/* 1. Atributos de Entidad */}
                <Tooltip title="Filtros por Atributos de Entidad" placement="left">
                  <IconButton
                    onClick={() => toggleFiltro('proveedor')}
                    sx={{
                      color: mostrarFiltroProveedor || Object.values(atributosEntidadFiltro).filter(v => v).length > 0 ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                      "& svg": {
                        color: mostrarFiltroProveedor || Object.values(atributosEntidadFiltro).filter(v => v).length > 0 ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                      },
                      bgcolor: mostrarFiltroProveedor ? alpha("rgb(244, 177, 14)", 0.1) : "transparent",
                      "&:hover": {
                        bgcolor: alpha("rgb(244, 177, 14)", 0.15),
                      },
                    }}
                  >
                    <Badge 
                      badgeContent={Object.values(atributosEntidadFiltro).filter(v => v).length > 0 ? 1 : 0} 
                      color="secondary"
                    >
                      <PersonIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>

                {/* 2. Estado */}
                <Tooltip title="Filtros de Estado" placement="left">
                  <IconButton
                    onClick={() => toggleFiltro('estado')}
                    sx={{
                      color: mostrarFiltroEstado || estadoSeleccionado ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                      "& svg": {
                        color: mostrarFiltroEstado || estadoSeleccionado ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                      },
                      bgcolor: mostrarFiltroEstado ? alpha("rgb(244, 177, 14)", 0.1) : "transparent",
                      "&:hover": {
                        bgcolor: alpha("rgb(244, 177, 14)", 0.15),
                      },
                    }}
                  >
                    <Badge 
                      badgeContent={estadoSeleccionado ? 1 : 0} 
                      color="secondary"
                    >
                      <CheckCircleIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>

                {/* 3. Montos */}
                <Tooltip title="Filtros de Montos" placement="left">
                  <IconButton
                    onClick={() => toggleFiltro('montos')}
                    sx={{
                      color: mostrarFiltroMontos || minCompras > 0 || maxCompras > 0 ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                      "& svg": {
                        color: mostrarFiltroMontos || minCompras > 0 || maxCompras > 0 ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                      },
                      bgcolor: mostrarFiltroMontos ? alpha("rgb(244, 177, 14)", 0.1) : "transparent",
                      "&:hover": {
                        bgcolor: alpha("rgb(244, 177, 14)", 0.15),
                      },
                    }}
                  >
                    <Badge badgeContent={minCompras > 0 || maxCompras > 0 ? 1 : 0} color="secondary">
                      <AttachMoneyIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>

                {/* 4. Mostrar Eliminados */}
                <Tooltip title={mostrarDeletes ? "Ocultar eliminados" : "Mostrar eliminados"} placement="left">
                  <IconButton
                    onClick={() => {
                      const nuevoEstado = !mostrarDeletes
                      setMostrarDeletes(nuevoEstado)
                      // Llamar a fetchData directamente con el nuevo estado para evitar problemas de sincronización
                      fetchData(
                        startDate,
                        1,
                        endDate,
                        proveedor,
                        selectedUbicacionFiltro,
                        minCompras,
                        maxCompras,
                        100,
                        estadoSeleccionado,
                        nuevoEstado,
                        idTransaccionSearch,
                        atributosEntidadFiltro
                      )
                    }}
                    sx={{
                      color: mostrarDeletes ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                      "& svg": {
                        color: mostrarDeletes ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                      },
                      bgcolor: mostrarDeletes ? alpha("rgb(244, 177, 14)", 0.1) : "transparent",
                      "&:hover": {
                        bgcolor: alpha("rgb(244, 177, 14)", 0.15),
                      },
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>

                <Divider sx={{ my: 1 }} />

              </Box>
            </Box>
          </Drawer>
        )}

        {/* Contenedor de la tabla (scrollable siempre dentro de la tarjeta) */}
        <Paper
          sx={{
            flex: 1,
            m: 1,
            borderRadius: 3,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            position: "relative",
            overflow: "hidden",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
            <DataGrid
key={isNavbarCollapsed ? "collapsed" : "expanded"}
              className="tablaVentasDataGrid"
              rows={sortedData}
              columns={columns}
              getRowId={(row) => row.id}
              getRowHeight={() => 35}
              headerHeight={35}  
              pageSize={5}
              sx={{
                width: "100%",
                border: 0,
                "& .MuiDataGrid-footerContainer": {
                  display: "none !important",
                },
                "& .MuiDataGrid-columnHeaders": { //MANEJA CSS HEADER
                  backgroundColor: "#5b5b5f",
                  color: "white",
                  borderBottom: "2px solid #333",
                  height: "45px !important",
                  minHeight: "45px !important",
                  maxHeight: "45px !important",
                },
                "& .MuiDataGrid-columnHeader": { //MANEJA CSS HEADER
                  height: "45px !important",
                  minHeight: "45px !important",
                  maxHeight: "45px !important",
                },
                "& .MuiDataGrid-columnHeaderTitle": { //MANEJA CSS HEADER
                  color: "white !important",
                  lineHeight: "45px !important",
                },
                "& .MuiDataGrid-columnHeaderTitle .MuiDataGrid-menuIconButton": {
                  color: "white !important",
                },
                "& .MuiDataGrid-menuIconButton": {
                  color: "rgb(244, 177, 14) !important",
                },
                "& .MuiInputBase-root": {
                  color: "rgb(244, 177, 14) !important",
                },
                "& .MuiTablePagination-root": {
                  color: "black !important",
                },
                "& .MuiSelect-icon": {
                  color: "rgb(244, 177, 14) !important",
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #ccc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                },
                "& .MuiDataGrid-row": {
                  
                  "&:nth-of-type(even)": {
                    backgroundColor: "#F7F7F7",
                  },
                  "&:hover": {
                    backgroundColor: "#E3EAFD",
                    transition: "background 0.3s ease",
                  },
                },
                "& .row-devolucion": {
                  backgroundColor: alpha("#9e9e9e !important", 0.05),
                  "&:hover": {
                    backgroundColor: alpha("#9e9e9e !important", 0.1),
                  },
                },
                "& .MuiDataGrid-virtualScroller": {
                  lineHeight: "none !important",
                  height: "10px !imporant",
                },
                "& .MuiDataGrid-footerContainer": {
                  position: "sticky",
                  bottom: 0,
                  backgroundColor: "#ffffff",
                  borderTop: "1px solid #ddd",
                  zIndex: 10,
                },
                "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                  width: "2px",
                },
                "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                  backgroundColor: "#888",
                  borderRadius: "10px",
                },
              }}
              components={{ Footer: CustomFooter }}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0
                  ? "row-color-alt"
                  : "row-color-default"
              }
            />
        </Paper>

        {/* Paginación */}
        <Box sx={{ flexShrink: 0, p: 1 }}>
          <CustomPagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={totalCount}
            montoTotalTransacciones={montoTotalTransacciones}
            vieneDe={"tablaCompras"}
            onPageChange={(newPage) => setPage(newPage)}
          />
        </Box>

        <ModalMedioDePago
          open={abrirMedioDePago}
          handleClose={handleCloseMedioDePago}
          precioTotal={precioTotal}
          transaccionSeleccionada={selectedTransaccion}
          onConfirmarPago={handleConfirmarPago}
        />

        {/* Modal de visualización */}
        <ModalViewTablaCompra
          openModal={openModal}
          handleCloseModal={handleCloseModal}
          selectedTransaccion={selectedTransaccion}
          selectedTransaccionEntidad={selectedTransaccionEntidad}
          transaccionItems={transaccionItems}
          compraItems={compraItems}
          montoTotalTransacciones={totalCompras}
        />
      </ThemeProvider>
    </div>
  );
}

